import { useState, useMemo } from 'react';
import { Loader2, Package, Search, Check } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useTakeItem } from '../hooks/useWarehouse';
import { useWeeklyVisits } from '@/features/visits/hooks/useWeeklyVisits';
import type { WarehouseItem } from '@/api/warehouse';
import type { OwnershipFilterValue } from '@/api/visits';

interface TakeItemSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: WarehouseItem | null;
  onSuccess?: () => void;
}

type FilterValue = OwnershipFilterValue[] | 'all';

const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

function loadOwnershipFilter(): FilterValue {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore
  }
  return ['own'];
}

export function TakeItemSheet({
  open,
  onOpenChange,
  item,
  onSuccess,
}: TakeItemSheetProps) {
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  // Use the same filter as visits page
  const ownershipFilter = useMemo(() => loadOwnershipFilter(), []);
  const { data: weeklyData, isLoading: clientsLoading } = useWeeklyVisits(ownershipFilter);

  const takeItem = useTakeItem();

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!weeklyData?.visits) return [];
    const q = search.toLowerCase().trim();
    if (!q) return weeklyData.visits;
    return weeklyData.visits.filter((v) =>
      v.client.name.toLowerCase().includes(q)
    );
  }, [weeklyData?.visits, search]);

  const selectedClient = weeklyData?.visits.find(
    (v) => v.client.id === selectedClientId
  );

  const isValid = item?.id && selectedClientId;

  const handleSubmit = () => {
    if (!isValid || !item?.id || !selectedClient) return;

    takeItem.mutate(
      {
        id: item.id,
        clientId: selectedClient.client.id,
        clientName: selectedClient.client.name,
      },
      {
        onSuccess: () => {
          setSelectedClientId(null);
          setSearch('');
          onSuccess?.();
        },
      }
    );
  };

  // Reset state when sheet closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedClientId(null);
      setSearch('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Забрать: {item?.name}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-4 space-y-4">
          {/* Item info */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="font-medium">{item?.name}</div>
            {item?.barcode && (
              <div className="text-sm text-muted-foreground">
                Штрихкод: {item.barcode}
              </div>
            )}
          </div>

          {/* Client search */}
          <div className="space-y-2">
            <Label>Выберите клиента</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск клиента..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Client list */}
          <div className="border rounded-md max-h-64 overflow-y-auto">
            {clientsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {search ? 'Клиенты не найдены' : 'Нет клиентов'}
              </div>
            ) : (
              filteredClients.map((visit) => (
                <button
                  key={visit.client.id}
                  type="button"
                  onClick={() => setSelectedClientId(visit.client.id)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 text-left border-b last:border-b-0 transition-colors',
                    selectedClientId === visit.client.id && 'bg-accent'
                  )}
                >
                  <div
                    className={cn(
                      'h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                      selectedClientId === visit.client.id
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground'
                    )}
                  >
                    {selectedClientId === visit.client.id && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <span className="truncate">{visit.client.name}</span>
                </button>
              ))
            )}
          </div>

          {/* Selected client info */}
          {selectedClient && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-sm font-medium text-green-800">
                Выбран: {selectedClient.client.name}
              </div>
            </div>
          )}
        </div>

        {/* Submit button */}
        <div className="pt-4 border-t mt-4">
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!isValid || takeItem.isPending}
          >
            {takeItem.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Package className="h-4 w-4 mr-2" />
            )}
            Забрать товар
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
